import { DATE_FORMATS } from '../constants';

/**
 * Format a date string to a readable format
 * @param dateString - ISO date string
 * @param format - Format type (short, long, or custom)
 * @returns Formatted date string
 */
export const formatDate = (dateString: string, format: keyof typeof DATE_FORMATS = 'SHORT'): string => {
  const date = new Date(dateString);
  
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  switch (format) {
    case 'SHORT':
      return date.toLocaleDateString(DATE_FORMATS.DISPLAY, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    case 'LONG':
      return date.toLocaleDateString(DATE_FORMATS.DISPLAY, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    default:
      return date.toLocaleDateString(DATE_FORMATS.DISPLAY);
  }
};

/**
 * Get relative time string (e.g., "2 hours ago")
 * @param dateString - ISO date string
 * @returns Relative time string
 */
export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minute${Math.floor(diffInSeconds / 60) > 1 ? 's' : ''} ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) > 1 ? 's' : ''} ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} day${Math.floor(diffInSeconds / 86400) > 1 ? 's' : ''} ago`;
  
  return formatDate(dateString, 'SHORT');
};

/**
 * Check if a date is in the future
 * @param dateString - ISO date string
 * @returns Boolean indicating if date is in the future
 */
export const isFutureDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return date > new Date();
};

/**
 * Get date range string
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Formatted date range string
 */
export const getDateRange = (startDate: string, endDate: string): string => {
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

/**
 * Format a Date object for API requests
 * Converts to the format expected by the API: "2025-09-11 05:10:49.956+00"
 * @param date - Date object
 * @returns Formatted date string for API
 */
export const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
  
  // Get timezone offset in the format +00:00
  const offset = date.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offset) / 60);
  const offsetMinutes = Math.abs(offset) % 60;
  const offsetSign = offset <= 0 ? '+' : '-';
  const timezone = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}${timezone}`;
};

/**
 * Format a Date object for API "from" parameter (start of day)
 * Sets time to 00:00:00.000 for the start of the selected day
 * @param date - Date object
 * @returns Formatted date string for API "from" parameter
 */
export const formatDateForAPIFrom = (date: Date): string => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return formatDateForAPI(startOfDay);
};

/**
 * Format a Date object for API "to" parameter (end of day)
 * Sets time to 23:59:59.999 for the end of the selected day
 * @param date - Date object
 * @returns Formatted date string for API "to" parameter
 */
export const formatDateForAPITo = (date: Date): string => {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return formatDateForAPI(endOfDay);
};

